import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, takeUntil, finalize } from 'rxjs';

import { ConsentResponse, ConsentType } from '../../../../../../../../libs/shared/src/lib/models/consent.model';
import { ConsentService } from '../../../../../../../../libs/shared/src/lib/services/consent.service';

import { CardComponent } from '../../../../components/card/card.component';
import { formatDateLocalized } from '../../../../utils/date-utils';
import { LanguageRoutingService } from 'apps/sameta-fe-jobs/src/app/services/language-routing.service';
import { Router, RouterModule } from '@angular/router';
// import { getConsents } from '../../../../../../../../libs/shared/src/lib/i18n/consents/index'

@Component({
  selector: 'app-consent-management',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, TranslocoModule, MatProgressSpinnerModule, CardComponent],
  templateUrl: './consent-management.component.html',
  styleUrls: ['./consent-management.component.scss'],
})
export class ConsentManagementComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  consents: ConsentResponse[] = [];
  loading = true;
  error: string | null = null;
  updatingConsents = new Set<ConsentType>();

  // Consent type enum for template
  ConsentType = ConsentType;

  constructor(
    private readonly consentService: ConsentService,
    private readonly translocoService: TranslocoService,
    private router: Router,
    public languageRoutingService: LanguageRoutingService
  ) {}

  ngOnInit(): void {
    this.loadConsents();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load all user consents from the backend
   */
  private loadConsents(): void {
    this.loading = true;
    this.error = null;

    this.consentService
      .getUserConsents(true)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (consents: ConsentResponse[]) => {
          this.consents = consents;
        },
        error: (error: any) => {
          console.error('Failed to load consents:', error);
          this.error = this.translocoService.translate('pages.settings.consent.loadError');
        },
      });
  }

  /**
   * Handle consent toggle (create or update)
   */
  onConsentToggle(consentType: ConsentType, granted: boolean): void {
    const consent = this.consents.find((c) => c.consentType === consentType);
    if (!consent) return;

    const previousGranted = consent.granted;
    this.updatingConsents.add(consentType);

    this.consentService
      .setUserConsent(this.consents, consentType, granted, {
        jobsRequest: true,
        language: this.getConsentLanguage(),
        version: consent.version ?? '1.0',
        purposeDescription: this.translocoService.translate('descriptions.' + consentType),
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.updatingConsents.delete(consentType))
      )
      .subscribe({
        next: (updatedConsent: ConsentResponse) => {
          const index = this.consents.findIndex((c) => c.consentType === consentType);
          if (index !== -1) {
            this.consents[index] = updatedConsent;
          }
        },
        error: (error: any) => {
          console.error('Failed to update consent:', error);
          this.error = this.translocoService.translate('pages.settings.consent.updateError');
          consent.granted = previousGranted;
        },
      });
  }

  /**
   * Map current Transloco language to the backend consent language enum.
   */
  private getConsentLanguage(): string {
    const lang = this.translocoService.getActiveLang().toUpperCase();
    return ['EN', 'SK', 'CZ'].includes(lang) ? lang : 'EN';
  }

  /**
   * Check if a consent is currently being updated
   */
  isUpdating(consentType: ConsentType): boolean {
    return this.updatingConsents.has(consentType);
  }

  /**
   * Check if consent is required (cannot be disabled)
   */
  isRequired(consentType: ConsentType): boolean {
    return consentType === ConsentType.PRIVACY_POLICY || consentType === ConsentType.TERMS_OF_USE;
  }

  /**
   * Get essential consents (required)
   */
  getEssentialConsents(): ConsentResponse[] {
    return this.consents.filter((consent) => this.isRequired(consent.consentType));
  }

  /**
   * Get optional consents (not required)
   */
  getOptionalConsents(): ConsentResponse[] {
    return this.consents.filter((consent) => !this.isRequired(consent.consentType));
  }

  /**
   * Format date for display, aligned with the active app language so we
   * don't mix US and EU formats across the app.
   */
  formatDate(dateString?: string): string {
    if (!dateString) return '';
    return formatDateLocalized(dateString, this.translocoService.getActiveLang());
  }

  /**
   * Dismiss error message
   */
  dismissError(): void {
    this.error = null;
  }
}
